import React from 'react';
import yaml from 'js-yaml';
// renderers is imported separately so Markdoc isn't sent to the client
import Markdoc, {renderers} from '@markdoc/markdoc'

import {getSchema} from '/opt/buildhome/repo/node_modules/@markdoc/next.js/src/runtime.js';
/**
 * Schema is imported like this so end-user's code is compiled using build-in babel/webpack configs.
 * This enables typescript/ESnext support
 */
const config = {};
import * as tags from '/opt/buildhome/repo/markdoc/tags.js'
import * as nodes from '/opt/buildhome/repo/markdoc/nodes.js'
const functions = {};
const schema = {
tags: tags ? (tags.default || tags) : {},
nodes: nodes ? (nodes.default || nodes) : {},
functions: functions ? (functions.default || functions) : {},
...(config ? (config.default || config) : {}),
};

/**
 * Source will never change at runtime, so parse happens at the file root
 */
const source = "---\ntitle: Message Rating\ndescription: Message rating enables you to rate a message in a conversation.\n---\n\n\n## Rate Message\nThis endpoint allows you to rate whether an AI generated message was good or bad through the API.\n\n\n```bash\nPOST /api.mendable.ai/v0/rateMessage\n```\n\nHere is an example request using cURL:\n\n```bash\ncurl -X POST https://api.mendable.ai/v0/rateMessage \\\n  -H \"Content-Type: application/json\" \\\n  -d '{\n        \"api_key\": \"YOUR_API_KEY\",\n        \"message_id\": 123,\n        \"rating_value\": 1\n      }'\n```\n\nor using Javascript:\n    \n```javascript\nconst url = \"https://api.mendable.ai/v0/rateMessage\";\n\nconst data = {\n  api_key: \"YOUR_API_KEY\",\n  message_id: 123,\n  rating_value: 1\n};\n\nfetch(url, {\n  method: \"POST\",\n  headers: {\n    \"Content-Type\": \"application/json\",\n  },\n  body: JSON.stringify(data),\n})\n  .then((response) => response.json())\n  .then((data) => console.log(data))\n  .catch((error) => console.error(\"Error:\", error));\n\n```\n\n### Body Parameters\n\n| Parameter      | Type   | Description                               |\n| -------------- | ------ | ----------------------------------------- |\n| `api_key`      | string | The API key associated with your project. |\n| `message_id`   | int    | The ID of the message to be rated.        |\n| `rating_value` | int    | 1 for good, -1 for bad. The rating value for the message.         |\n\n\n\n### Response\n\n```bash\nHTTP/1.1 200 OK\nContent-Type: text/plain\n\nMessage rating updated\n```\n\n\n\n### Success\n\n- Status code: `200`\n- Response body: `\"Message rating updated\"`\n\n### Errors\n\n- Status code: `400`\n  - Missing `api_key` parameter\n  - Invalid API key or project not found\n\n- Status code: `500`\n  - Error updating message rating\n  - Permission denied to rate the message\n";
const filepath = "/mendable-api/rating.md";
const ast = Markdoc.parse(source);

/**
 * Like the AST, frontmatter won't change at runtime, so it is loaded at file root.
 * This unblocks future features, such a per-page dataFetchingFunction.
 */
const frontmatter = ast.attributes.frontmatter
  ? yaml.load(ast.attributes.frontmatter)
  : {};

const {components, ...rest} = getSchema(schema)

export async function getStaticProps(context) {
  const partials = {};

  // Ensure Node.transformChildren is available
  Object.keys(partials).forEach((key) => {
    partials[key] = Markdoc.parse(partials[key]);
  });

  const cfg = {
    ...rest,
    variables: {
      ...(rest ? rest.variables : {}),
      // user can't override this namespace
      markdoc: {frontmatter},
      // Allows users to eject from Markdoc rendering and pass in dynamic variables via getServerSideProps
      ...(context.variables || {})
    },
    partials,
    source,
  };

  /**
   * transform must be called in dataFetchingFunction to support server-side rendering while
   * accessing variables on the server
   */
  const content = Markdoc.transform(ast, cfg);

  return {
    // Removes undefined
    props: JSON.parse(
      JSON.stringify({
        markdoc: {
          content,
          frontmatter,
          file: {
            path: filepath
          }
        },
      })
    ),
  };
}

export default function MarkdocComponent(props) {
  // Only execute HMR code in development
  return renderers.react(props.markdoc.content, React, {
    components: {
      ...components,
      // Allows users to override default components at runtime, via their _app
      ...props.components,
    },
  });
}
